/**
 * Pure whitespace / renewal rules for the Hierarchy tab's "Color by" and "Renewals due" lenses.
 * Shared so the client and tests agree on one definition; everything derives from the node's own
 * mirrored columns (nothing is recomputed server-side).
 */

/**
 * The 5-way account status the "Customer status" color-by paints. Precedence, first match wins:
 * customer (ARR > 0) > open pipeline > churned > prospect with contacts > untouched. Churned sits
 * BELOW open pipeline so a churned account with a live deal reads as open pipeline (a win-back in
 * flight is the more actionable fact), and ABOVE prospect so it never reads as fresh whitespace.
 */
export const WHITESPACE_STATUSES = ['customer', 'open_pipeline', 'churned', 'prospect_contacts', 'untouched'] as const;
export type WhitespaceStatus = (typeof WHITESPACE_STATUSES)[number];

/** `account_type` values Salesforce uses for a lapsed customer (ARR is 0 by definition). */
const CHURNED_ACCOUNT_TYPES: readonly string[] = ['Churned Customer', 'Churned Account'];

export interface WhitespaceInput {
  arr_active_subscriptions: number | null;
  open_pipeline: number | null;
  account_type: string | null;
  contact_count: number;
}

export function whitespaceStatusOf(node: WhitespaceInput): WhitespaceStatus {
  if ((node.arr_active_subscriptions ?? 0) > 0) return 'customer';
  if ((node.open_pipeline ?? 0) > 0) return 'open_pipeline';
  if (node.account_type !== null && CHURNED_ACCOUNT_TYPES.includes(node.account_type)) return 'churned';
  if (node.contact_count > 0) return 'prospect_contacts';
  return 'untouched';
}

/**
 * "Renews in Nd" badge horizon (the badge shows on nodes whatever lens is active). Neither Salesforce (`Enterprise_Renewal_Date__c` is just "End date of
 * current Enterprise contract", no window field/formula on Account or Opportunity) nor Terra
 * (`renewal_date` is the raw date) defines a window, so this is OUR constant. 90 matches the one
 * renewal horizon the platform already uses: Book Carve's renewal lock (`renewingWithinDays`
 * default 90, mini-apps/book-carve/index.tsx LOCK_DEFAULTS).
 */
export const RENEWAL_WINDOW_DAYS = 90;

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Whole calendar days from `today` (its LOCAL date) to a `YYYY-MM-DD` date; NaN when malformed. */
export function daysUntilRenewal(date: string, today: Date): number {
  const m = DATE_RE.exec(date);
  if (!m) return Number.NaN;
  const target = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const start = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((target - start) / 86_400_000);
}

/** Days to renewal when it falls in `[0, windowDays]`; null for none, past, malformed or too far out. */
export function renewalDaysOf(date: string | null, today: Date, windowDays: number = RENEWAL_WINDOW_DAYS): number | null {
  if (!date) return null;
  const days = daysUntilRenewal(date, today);
  return Number.isFinite(days) && days >= 0 && days <= windowDays ? days : null;
}

/** ARR on the line at renewal: the contracted figure, else what the account is paying today. */
export function arrAtRiskOf(node: { contracted_arr: number | null; arr_active_subscriptions: number | null }): number | null {
  if (node.contracted_arr != null && node.contracted_arr > 0) return node.contracted_arr;
  return node.arr_active_subscriptions;
}

export type HealthTone = 'success' | 'warning' | 'danger';

const HEALTH_TONES: Readonly<Record<string, HealthTone>> = { green: 'success', yellow: 'warning', red: 'danger' };

export function healthToneOf(rating: string | null): HealthTone | null {
  return rating ? HEALTH_TONES[rating.trim().toLowerCase()] ?? null : null;
}

/** A Salesforce multiselect picklist arrives as one `;`-delimited string. */
export function splitMultiselect(raw: string | null): string[] {
  return (raw ?? '').split(';').map(s => s.trim()).filter(s => s.length > 0);
}

/**
 * The clickable legend's lens: accounts whose status is one of `statuses` (multi-select unions
 * them). `null` = nothing selected = the lens is off.
 */
export function statusIdsOf(
  nodes: ReadonlyArray<{ id: string } & WhitespaceInput>,
  statuses: readonly WhitespaceStatus[],
): Set<string> | null {
  if (statuses.length === 0) return null;
  return new Set(nodes.filter(n => statuses.includes(whitespaceStatusOf(n))).map(n => n.id));
}
